import { connect, ConnectedProps } from 'react-redux';

import { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountStatus } from 'dashboard/models/account-status';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  addAccountStatus,
  loadAccountStatuses,
} from 'dashboard/redux/thunk-actions/account-statuses';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';

export interface ConnectedAccountStatusSelectProps extends PropsFromRedux {
  id?: string;
  inputId?: string;
  className?: string;
  name?: string;
  value: string;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  returnName?: boolean;
  onChange: (values: string) => void;
}

const ConnectedAccountStatusSelect = ({
  id,
  inputId,
  className,
  name,
  value,
  placeholder,
  hasError,
  disabled,
  onChange,
  accountStatuses,
  returnName,
  ...props
}: ConnectedAccountStatusSelectProps) => {
  const { loading: isLoading, callback: handleLoadAccountStatuses } = useApiLoader({
    loader: () => props.loadAccountStatuses(),
    deps: [props.loadAccountStatuses],
    onError: () => props.showMessage('Failed to load account statuses.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleAddAccountStatus, loading: isAdding } = useApiCallback(
    async (name: string) => {
      const { data: accountStatus } = await SessionClientDashboard.postResourceCreate<
        AccountStatus,
        {
          type: ApiModel.AccountStatus;
          attributes: { name: string };
        }
      >({
        type: ApiModel.AccountStatus,
        attributes: { name },
      });
      props.showMessage('Successfully added new account status.', MessageType.Success);
      await handleLoadAccountStatuses();
      onChange(accountStatus.attributes.name);
    },
    [isLoading, onChange, handleLoadAccountStatuses],
  );

  const options = accountStatuses.map((accountStatus) => {
    return {
      label: accountStatus.attributes.name,
      value: returnName ? accountStatus.attributes.name : accountStatus.id,
    };
  });

  return (
    <ReactCreatableSelect
      id={id}
      inputId={inputId}
      className={className}
      name={name}
      value={value}
      placeholder={placeholder}
      hasError={hasError}
      disabled={disabled || isLoading || isAdding}
      isMulti={false}
      isSearchable={true}
      portal={true}
      options={options}
      onChange={onChange}
      onCreateOption={handleAddAccountStatus}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountStatuses: state.accountStatuses.accountStatuses,
  };
};

const mapDispatchToProps = {
  loadAccountStatuses,
  addAccountStatus,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConnectedAccountStatusSelect);
